import { G } from '@mobily/ts-belt';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { func, oneOfType, string } from 'prop-types';
import { useMemo } from 'react';
import { FieldProps, Link, LinkProps, RaRecord, useRecordContext } from 'react-admin';

import { PermissionActionT } from '@Common/types/bo/authorization';
import { BOResourceAll } from '@Common/types/bo_external/resources';

import { useCanAccess } from '@sh4res/bo-common/hooks';
import { Resources } from '@sh4res/bo-common/utils';

export type IdFieldQueryParamsArrayLike = Array<unknown> | ReadonlyArray<unknown>;

export type IdFieldProps<T extends RaRecord> = Exclude<FieldProps<T>, 'reference'> & {
  reference: Resources | ((record: T) => string);
  target?: string;
  rel?: string;
  linkProps?: Partial<LinkProps>;
  referencePath?: string;
  emptyText?: string;
  textSource?: FieldProps<T>['source'];
  'data-testid'?: string;
  queryParams?: Record<string, string | number | boolean | IdFieldQueryParamsArrayLike>;
  customText?: string | number;
};

const IdLink = styled(Link)`
  color: ${({ theme }) => theme.palette.secondary.main};
  margin-left: 0;
  margin-right: 0;
  text-decoration: underline;
`;

export const IdField = <T extends RaRecord = RaRecord>(props: IdFieldProps<T>) => {
  const {
    reference,
    referencePath = 'show',
    source = 'id',
    target = '_blank',
    rel = 'noopener noreferrer',
    linkProps,
    resource: _,
    emptyText,
    textSource,
    queryParams,
    customText,
    ...rest
  } = props;
  const record = useRecordContext(props);

  const value: string | number | undefined = get(record, source);

  const text = customText ?? get(record, textSource ?? source);

  const to = useMemo(() => {
    if (G.isNullable(record)) {
      return '';
    }

    const params = new URLSearchParams(queryParams as Record<string, string>).toString();

    const queryString = params ? `?${params}` : '';

    return typeof reference === 'string'
      ? `${reference.startsWith('/') ? '' : '/'}${reference}/${String(value)}/${referencePath}${queryString}`
      : reference(record);
  }, [reference, record, value, referencePath, queryParams]);

  const canNavigateTo = useCanAccess({
    resource: to.split(value as string)[0].slice(1, -1) as BOResourceAll,
    action: referencePath as PermissionActionT,
  });

  if (G.isNullable(text) || G.isNullable(value) || G.isNullable(record)) {
    return emptyText ?? null;
  }

  return (
    /* oxlint-disable jsx-a11y/no-static-element-interactions */
    <span onClick={(event) => event.stopPropagation()}>
      {/* oxlint-enable jsx-a11y/no-static-element-interactions */}{' '}
      <Typography {...rest}>
        {canNavigateTo ? (
          <IdLink
            {...linkProps}
            to={linkProps?.to ?? to}
            target={linkProps?.target ?? target}
            rel={linkProps?.rel ?? rel}>
            {text}
          </IdLink>
        ) : (
          text
        )}
      </Typography>
    </span>
  );
};

IdField.propTypes = {
  source: string,
  resource: string,
  reference: oneOfType([string, func]).isRequired,
  target: string,
  rel: string,
};

IdField.defaultProps = {
  component: 'span',
  variant: 'body2',
};
